/** Pure contracts for the global Image Asset Picker (upload-first / multi-select). */

import type { ImagePickerDetail, ImagePickerListItem, PickerResourceType, PickerSourceFilter } from '../../api'

export type PickerSelectionMode = 'single' | 'multiple'

export interface UploadBatchSuccess {
  pickerItemId: string
}

export type PickerListFilterQuery = {
  resourceType: PickerResourceType
  source: PickerSourceFilter
  search: string
}

/** Default grid capacity: 5 columns × 4 rows = 20 visual cells per page. */
export const PICKER_DEFAULT_PAGE_SIZE = 20

/** VistoeraTASK-00408 — same bound as Canvas (`siteAssetUpload.ts`). */
export { CANONICAL_SITE_ASSET_UPLOAD_CONCURRENCY } from '../../siteAssetUpload'

/**
 * When page 1 renders the Upload action tile, it consumes one grid cell.
 * Real assets per UI page: page1 = pageSize-1, page2+ = pageSize.
 */
export function pickerFirstPageAssetLimit(pageSize: number, hasUploadTileOnFirstPage: boolean): number {
  return hasUploadTileOnFirstPage ? Math.max(0, pageSize - 1) : pageSize
}

export function pickerListOffset(
  uiPage: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): number {
  if (uiPage <= 1) return 0
  const firstLimit = pickerFirstPageAssetLimit(pageSize, hasUploadTileOnFirstPage)
  return firstLimit + (uiPage - 2) * pageSize
}

export function pickerListLimit(
  uiPage: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): number {
  if (hasUploadTileOnFirstPage && uiPage === 1) return pickerFirstPageAssetLimit(pageSize, true)
  return pageSize
}

export function pickerTotalPages(
  total: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): number {
  if (total <= 0) return 1
  if (!hasUploadTileOnFirstPage) return Math.max(1, Math.ceil(total / pageSize))
  const firstLimit = pickerFirstPageAssetLimit(pageSize, true)
  if (total <= firstLimit) return 1
  return 1 + Math.ceil((total - firstLimit) / pageSize)
}

/** Ant Design Pagination `total` so page count matches pickerTotalPages. */
export function pickerAntdPaginationTotal(
  total: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): number {
  if (total <= 0) return 1
  if (!hasUploadTileOnFirstPage) return total
  const pages = pickerTotalPages(total, pageSize, true)
  const firstLimit = pickerFirstPageAssetLimit(pageSize, true)
  if (pages <= 1) return total
  return (pages - 1) * pageSize + firstLimit
}

export function pickerListRequestParams(
  uiPage: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): { page: number; page_size: number; offset: number } {
  return {
    page: uiPage,
    page_size: pickerListLimit(uiPage, pageSize, hasUploadTileOnFirstPage),
    offset: pickerListOffset(uiPage, pageSize, hasUploadTileOnFirstPage),
  }
}

/**
 * VistoeraTASK-00068 — page to display after a delete changes ``total``.
 *
 * Clamps the current page down when it no longer exists (e.g. the last
 * asset on the last page was deleted), otherwise keeps the user on the
 * same page number so the canonical offset re-fetch can refill it from
 * whatever used to be on the next page. Never jumps to page 1 just
 * because *some* page shrank — only clamps when the page itself is now
 * out of range.
 */
export function pickerPageAfterDelete(
  currentPage: number,
  newTotal: number,
  pageSize: number,
  hasUploadTileOnFirstPage: boolean,
): number {
  const totalPages = pickerTotalPages(newTotal, pageSize, hasUploadTileOnFirstPage)
  return Math.min(Math.max(1, currentPage), totalPages)
}

/** After a batch upload succeeds, compute next selection + active id. */
export function selectionAfterUploadBatch(args: {
  mode: PickerSelectionMode
  maxSelection: number
  previousSelectedIds: string[]
  successes: UploadBatchSuccess[]
}): { selectedIds: string[]; activeAssetId: string | null } {
  const ids = args.successes.map((s) => s.pickerItemId).filter(Boolean)
  if (ids.length === 0) {
    return { selectedIds: args.previousSelectedIds, activeAssetId: null }
  }
  const last = ids[ids.length - 1]
  if (args.mode === 'single') {
    return { selectedIds: [last], activeAssetId: last }
  }
  const merged: string[] = []
  for (const id of [...args.previousSelectedIds, ...ids]) {
    if (!merged.includes(id)) merged.push(id)
    if (merged.length >= args.maxSelection) break
  }
  return { selectedIds: merged, activeAssetId: last }
}

/** Mirror backend picker filter semantics for optimistic insert decisions. */
export function pickerItemMatchesFilters(
  item: Pick<ImagePickerListItem, 'resource_type' | 'source' | 'filename'>,
  query: PickerListFilterQuery,
): boolean {
  if (query.resourceType !== 'all' && item.resource_type !== query.resourceType) return false
  if (query.source !== 'all' && item.source !== query.source) return false
  const needle = query.search.trim().toLowerCase()
  if (needle && !(item.filename || '').toLowerCase().includes(needle)) return false
  return true
}

export function dedupePickerItemsById(items: ImagePickerListItem[]): ImagePickerListItem[] {
  const seen = new Set<string>()
  const out: ImagePickerListItem[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

/** Prepend uploaded items for recent-first page 1 without duplicates. */
export function mergeUploadedIntoRecentList(
  existing: ImagePickerListItem[],
  uploaded: ImagePickerListItem[],
  maxItems: number,
): ImagePickerListItem[] {
  return dedupePickerItemsById([...uploaded, ...existing]).slice(0, maxItems)
}

export function pickerDetailToListItem(detail: ImagePickerDetail): ImagePickerListItem {
  return {
    id: detail.id,
    reference_type: detail.reference_type,
    asset_id: detail.asset_id,
    site_id: detail.site_id,
    resource_type: detail.resource_type,
    resource_type_label: detail.resource_type_label,
    source: detail.source,
    source_label: detail.source_label,
    filename: detail.filename,
    width: detail.width,
    height: detail.height,
    format: detail.format,
    size_bytes: detail.size_bytes,
    created_at: detail.created_at,
    thumbnail_url: detail.thumbnail_url,
    family_id: detail.family_id,
    status: detail.status,
    created_by_user_id: detail.created_by_user_id ?? null,
    visibility: detail.visibility,
    collection_id: detail.collection_id ?? null,
  }
}

/** Deterministic non-overlapping grid near an anchor (viewport center / click). */
export function placeImageAssetGrid(
  count: number,
  anchor: { x: number; y: number },
  options: { colWidth?: number; rowHeight?: number; cols?: number } = {},
): Array<{ x: number; y: number }> {
  const colWidth = options.colWidth ?? 280
  const rowHeight = options.rowHeight ?? 300
  const cols = Math.max(1, options.cols ?? 3)
  const rows = Math.ceil(count / cols)
  const totalW = Math.min(count, cols) * colWidth - colWidth
  const totalH = rows * rowHeight - rowHeight
  const startX = anchor.x - totalW / 2
  const startY = anchor.y - totalH / 2
  return Array.from({ length: count }, (_, i) => ({
    x: startX + (i % cols) * colWidth,
    y: startY + Math.floor(i / cols) * rowHeight,
  }))
}

export function nodesOverlap(a: { x: number; y: number }, b: { x: number; y: number }, minGap = 40): boolean {
  return Math.abs(a.x - b.x) < minGap && Math.abs(a.y - b.y) < minGap
}

/** Run async tasks with bounded concurrency (default 4). */
export async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const limit = Math.max(1, Math.min(concurrency, items.length || 1))
  const results: R[] = new Array(items.length)
  let next = 0
  async function runOne(): Promise<void> {
    while (next < items.length) {
      const i = next
      next += 1
      results[i] = await worker(items[i], i)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => runOne()))
  return results
}
